<template>
  <Modal
    v-model:visible="isVisible"
    class="event-modal"
    :header="
      t('batchAction.eventModal.title', {
        param: batchEvent.eventType
          ? t(`enumMap.ShipmentEventType.${batchEvent.eventType}`)
          : '',
      })
    "
    @keypress.enter="onSave"
    @keypress.esc="onCancel"
  >
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      input-id="event-date"
      class="field"
      :label="t('shipments.dates.actualDateTime')"
      :errors="v$.eventDate.$errors"
    >
      <RygenCalendar
        :id="inputId"
        v-model="eventDateTime"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :step-minute="15"
        date-format="mm/dd/yy"
        hour-format="12"
        show-icon
        show-time
        :placeholder="t('shipments.dates.actualDateTime')"
      />
    </InputWrapper>

    <InputWrapper
      v-slot="{ ariaProps, inputId }"
      input-id="event-comment"
      class="field"
      :label="t('common.comments')"
      optional
    >
      <Textarea
        :id="inputId"
        v-model="batchEvent.comment"
        v-bind="ariaProps"
        rows="5"
        cols="60"
        :placeholder="t('common.comments')"
        @keypress.enter="$event.stopPropagation()"
        @keypress.shift.enter="onSave"
      />
    </InputWrapper>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        mode="primary"
        icon="pi pi-save"
        :label="t('common.saveParam', { param: t('events.events') })"
        @click="onSave"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { DateTime } from 'luxon';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useValidations } from '@rygen/rygen-ui/composables';

import { ShipmentBatchEvent } from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import Textarea from 'primevue/textarea';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  modelValue: {
    type: Object as PropType<ShipmentBatchEvent>,
    default: () => new ShipmentBatchEvent(),
  },
});
const { modelValue } = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'save', shipmentBatchEvent: ShipmentBatchEvent): void;
  (event: 'update:model-value', newVal: ShipmentBatchEvent): void;
}>();

const { t } = useI18n();

const batchEvent: Ref<ShipmentBatchEvent> = ref(new ShipmentBatchEvent());
const rules = {
  eventType: {
    required: required(t('common.type')),
  },
  eventDate: {
    required: required(t('shipments.dates.actualDateTime')),
  },
};

const { v$, validate } = useValidations(batchEvent, rules);

const eventDateTime = computed({
  get() {
    const eventDate = batchEvent.value.eventDate;
    return eventDate ? DateTime.fromJSDate(eventDate) : undefined;
  },
  set(newVal: DateTime | undefined) {
    batchEvent.value.eventDate = newVal?.toJSDate();
  },
});

const onSave = async () => {
  if (await validate()) {
    emit('update:model-value', batchEvent.value);
    emit('save', batchEvent.value);
    isVisible.value = false;
  }
};

const onCancel = () => {
  emit('cancel');
  isVisible.value = false;
};

watch(
  modelValue,
  (modelValue: ShipmentBatchEvent) => {
    batchEvent.value = new ShipmentBatchEvent(modelValue);
    v$.value.$reset();
  },
  { deep: true, immediate: true },
);

watch(
  isVisible,
  (newVal) => {
    if (newVal) {
      batchEvent.value = new ShipmentBatchEvent(modelValue.value);
      v$.value.$reset();
    }
  },
  { immediate: true },
);
</script>
